//client/src/components/tiposContrato/TiposContrato.js
import React, { useState, useCallback, Fragment } from 'react';
import { useTiposContrato, useMutateTiposContrato } from '../../hooks/useTiposContrato';
import TipoContratoForm from './TipoContratoForm';
import TipoContratoRow from './TipoContratoRow';
import FiltroTiposContrato from './FiltroTipoContrato';
import { PlusCircle, FileText, Loader2, Filter as FilterIcon, XCircle, SquarePen, Trash2 } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const TiposContrato = () => {
    const [showForm, setShowForm] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [current, setCurrent] = useState(null);
    const [selectedTipo, setSelectedTipo] = useState(null);

    const { data: tiposContrato = [], isLoading, refetch } = useTiposContrato();
    const { deleteTipoContrato, isPending: isMutating } = useMutateTiposContrato();

    const handleAdicionar = useCallback(() => {
        setCurrent(null);
        setShowForm(true);
        setShowFilters(false);
    }, []);

    const handleEdit = useCallback((tipo) => {
        setCurrent(tipo);
        setShowForm(true);
        setShowFilters(false);
        setSelectedTipo(null);
    }, []);

    const handleFechar = useCallback(() => {
        setShowForm(false);
        setCurrent(null);
    }, []);

    const handleFiltrosClick = useCallback(() => {
        setShowFilters(p => !p);
        if (showForm) {
            setShowForm(false);
            setCurrent(null);
        }
    }, [showForm]);

    const handleDelete = useCallback((id) => {
        if (window.confirm('Tem certeza que deseja excluir este tipo de contrato?')) {
            deleteTipoContrato.mutate(id, {
                onSuccess: () => setSelectedTipo(null),
            });
        }
    }, [deleteTipoContrato]);

    const handleSaveSuccess = useCallback(() => {
        handleFechar();
        refetch();
    }, [handleFechar, refetch]);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<FileText />}
                    title="Tipos de Contrato"
                    actions={<>
                        {!showForm && (
                            <>
                                <button onClick={handleFiltrosClick} className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}>
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {showForm && (
                            <button onClick={handleFechar} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {showForm && <div className="content-panel"><TipoContratoForm current={current} onCloseForm={handleFechar} onSaveSuccess={handleSaveSuccess} /></div>}
                {showFilters && !showForm && <div className="content-panel"><h3 className="panel-title">Filtrar Tipos de Contrato</h3><FiltroTiposContrato /></div>}
            </div>

            {!showForm && (
                <div className="table-container">
                    <div className="table-wrapper">
                         {isLoading ? <div className="table-loading-state"><Loader2 size={24} className="animate-spin"/></div> : (
                             <table className="styled-table">
                                 <thead><tr><th>Nome</th><th>Código</th><th>Descrição</th><th className="text-right">Ações</th></tr></thead>
                                 <tbody>
                                     {tiposContrato.length > 0 ? (
                                         tiposContrato.map(t => <TipoContratoRow key={t._id} tipo={t} onOpenActions={setSelectedTipo} loading={isMutating} />)
                                     ) : (
                                         <tr><td colSpan="4" className="td-empty">Nenhum tipo de contrato encontrado.</td></tr>
                                     )}
                                 </tbody>
                             </table>
                         )}
                    </div>
                </div>
            )}

            <FabButton
                onClick={handleAdicionar}
                title="Adicionar Tipo de Contrato"
                visible={!showForm}
                disabled={isLoading}
                icon={<FileText size={26} className="icon"/>}
            />

            <ActionsDrawer
                open={!!selectedTipo}
                onClose={() => setSelectedTipo(null)}
                title={selectedTipo?.nome || 'Tipo de Contrato'}
                actions={selectedTipo ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <SquarePen size={20} className="icon" />,
                        onClick: () => handleEdit(selectedTipo),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedTipo._id),
                        destructive: true,
                        loading: isMutating,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default TiposContrato;
